const urlPackage = require("url");
const axios = require("axios");
const Logger = require("shared/services/Logger.ts");
const { getRequestor } = require("shared/services/requestorConfiguration.ts");
const {
  COOKIE_CUSTOMER_SESSION_INVALIDATED_NAME,
  isPublicMandatoryLoginRoute,
} = require("./mandatoryLogin");

const COOKIE_CUSTOMER_TOKEN_NAME = "d4customerToken";
const COOKIE_CUSTOMER_NAME_NAME = "d4customerName";
const COOKIE_CUSTOMER_DEVICE_NAME = "d4customerDevice";
const COOKIE_CUSTOMER_SIGNED_TOKEN_NAME = "d4customerSignedToken";
const COOKIE_CBT_TOKEN_NAME = "d4cbtToken";
const COOKIE_REQUESTOR_PUBLIC_KEY = "d4requestorPublicKey";
const CUSTOMER_SESSION_INVALIDATED_MAX_AGE = 60 * 1000;
const CUSTOMER_SESSION_COOKIE_NAMES = [
  COOKIE_CUSTOMER_TOKEN_NAME,
  COOKIE_CUSTOMER_NAME_NAME,
  COOKIE_CUSTOMER_DEVICE_NAME,
  COOKIE_CUSTOMER_SIGNED_TOKEN_NAME,
  COOKIE_CBT_TOKEN_NAME,
  "token",
  "username",
  "cbtToken",
];
const AUTHENTICATED_CUSTOMER_SESSION_COOKIE_NAMES = [
  COOKIE_CUSTOMER_NAME_NAME,
  COOKIE_CUSTOMER_SIGNED_TOKEN_NAME,
  COOKIE_CBT_TOKEN_NAME,
  "token",
  "username",
  "cbtToken",
];

function getFullUrlFunction(req, addPathname = true) {
  const getHeader = (headerName) => {
    if (!req) {
      return "";
    }

    if (typeof req.get === "function") {
      return req.get(headerName);
    }

    const normalizedHeaderName = headerName.toLowerCase();
    const headerValue =
      req?.headers?.[normalizedHeaderName] ?? req?.headers?.[headerName];

    if (Array.isArray(headerValue)) {
      return headerValue[0] || "";
    }

    return headerValue || "";
  };

  const host = getHeader("host");
  const forwardedProtocol = getHeader("x-forwarded-proto");
  const protocol =
    req?.protocol || (forwardedProtocol || "").split(",")[0].trim() || "https";
  const pathname = addPathname ? req?.originalUrl || req?.url || "/" : "/";

  if (!host) {
    // Next export can run with a non-Express request object.
    return pathname;
  }

  return urlPackage.format({
    protocol,
    host,
    pathname,
  });
}

function generateRandomNumber(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateRandomString() {
  const result = [];
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  const length = Math.random() * 15 + 10;
  for (let i = 0; i < length; i++) {
    result.push(
      characters.charAt(Math.floor(Math.random() * charactersLength))
    );
  }
  return result.join("");
}

function getUrlPathname(url = "") {
  return url.split("?")[0];
}

function isStaticAssetRequest(urlPath) {
  if (!urlPath) {
    return false;
  }

  if (urlPath === "/static/index.json") {
    return true;
  }

  return (
    urlPath.includes("/static/") ||
    urlPath.includes("/_next/") ||
    urlPath.includes("/__ENV.js") ||
    /\.[a-zA-Z0-9]+$/.test(urlPath)
  );
}

function isUnloggedPage({ fullUrl, urlPath }) {
  return !isPublicMandatoryLoginRoute(fullUrl || urlPath);
}

function isUnAllowedRegisterPage({ registrationEnabled, urlPath }) {
  return urlPath.includes("/register") && !registrationEnabled;
}

function isProtectedPage(urlPath) {
  return urlPath.includes("/profile") || urlPath.includes("/reservation");
}

function redirectTo(res, location) {
  if (!res || res.headersSent) {
    return true;
  }

  res.writeHead(302, {
    Location: location,
  });
  res.end();
  return true;
}

function getPassiveSessionId({ cookies, res }) {
  if (cookies?.d4passiveSessionId) {
    return cookies.d4passiveSessionId;
  }

  const generatedD4passiveSessionId = `1${generateRandomNumber(
    10000000,
    99999999
  )}`;

  if (res?.cookie) {
    res.cookie("d4passiveSessionId", generatedD4passiveSessionId, {
      httpOnly: false,
      expires: false,
      sameSite: "strict",
      secure: true,
    });
  }

  return generatedD4passiveSessionId;
}

function getAuthorizationObject({ req, customerToken }) {
  const requestorPublicKey = req?.cookies?.[COOKIE_REQUESTOR_PUBLIC_KEY];

  if (requestorPublicKey) {
    const authorization = {
      RequestorPublicKey: { $t: requestorPublicKey },
    };

    if (customerToken) {
      authorization.UserToken = { $t: customerToken };
    }

    return authorization;
  }

  const authorization = { Requestor: getRequestor(req.hostname) };

  if (customerToken) {
    authorization.UserToken = { $t: customerToken };
  }

  return authorization;
}

async function isCustomerTokenValid({ req, res, golApiUrl }) {
  const customerToken = req?.cookies?.[COOKIE_CUSTOMER_TOKEN_NAME];

  if (!customerToken || !golApiUrl) {
    return false;
  }

  const reqObj = {
    GolApi: {
      PassiveSessionId: getPassiveSessionId({ cookies: req.cookies, res }),
      Authorization: getAuthorizationObject({ req, customerToken }),
      Settings: { Localization: { Language: "cs", Country: "CZ" } },
      RequestDetail: {
        DetailCustomerRequest_1: {},
      },
    },
  };

  const response = await axios.post(golApiUrl, JSON.stringify(reqObj));

  if (response.data.GolApi.ResponseDetail.DetailCustomerError_1) {
    Logger.warn(
      `Invalid customerToken (${req.hostname}):`,
      JSON.stringify(response.data.GolApi.ResponseDetail.DetailCustomerError_1)
    );
    return false;
  }

  return true;
}

async function isLogged({ req, res, golApiUrl }) {
  if (
    !req?.cookies?.[COOKIE_CUSTOMER_TOKEN_NAME] ||
    !req?.cookies?.[COOKIE_CUSTOMER_NAME_NAME] ||
    !req?.cookies?.[COOKIE_CUSTOMER_DEVICE_NAME]
  ) {
    return false;
  }

  return isCustomerTokenValid({ req, res, golApiUrl });
}

function clearCustomerSessionCookies(req, res) {
  if (
    !CUSTOMER_SESSION_COOKIE_NAMES.some(
      (cookieName) => req?.cookies?.[cookieName]
    )
  ) {
    return false;
  }

  CUSTOMER_SESSION_COOKIE_NAMES.forEach((cookieName) =>
    res.clearCookie(cookieName)
  );
  res.cookie(COOKIE_CUSTOMER_SESSION_INVALIDATED_NAME, "true", {
    httpOnly: false,
    maxAge: CUSTOMER_SESSION_INVALIDATED_MAX_AGE,
    path: "/",
    sameSite: "strict",
    secure: true,
  });
  return true;
}

function hasAuthenticatedCustomerSessionMarker(req) {
  return AUTHENTICATED_CUSTOMER_SESSION_COOKIE_NAMES.some(
    (cookieName) => req?.cookies?.[cookieName]
  );
}

async function ensureValidCustomerToken({ req, res, golApiUrl }) {
  const hasCustomerName = Boolean(req?.cookies?.[COOKIE_CUSTOMER_NAME_NAME]);

  if (!hasCustomerName) {
    return true;
  }

  if (
    !req?.cookies?.[COOKIE_CUSTOMER_TOKEN_NAME] ||
    !req?.cookies?.[COOKIE_CUSTOMER_DEVICE_NAME]
  ) {
    clearCustomerSessionCookies(req, res);
    return true;
  }

  if (!(await isCustomerTokenValid({ req, res, golApiUrl }))) {
    Logger.warn(
      "Invalid tokens, clearing. Maybe you changed config.json and didn't restart app?"
    );

    clearCustomerSessionCookies(req, res);

    return redirectTo(res, "/");
  }

  return true;
}

async function passwordProtectionFunction({ ctx, reduxStore, getGOLAPIUrl }) {
  const req = ctx?.req;
  const res = ctx?.res;

  if (!req || !res || typeof getGOLAPIUrl !== "function") {
    return false;
  }

  const originalUrl = req.originalUrl || req.url || "";
  const urlPath = getUrlPathname(originalUrl);

  if (isStaticAssetRequest(urlPath)) {
    return false;
  }

  const golApiUrl = getGOLAPIUrl(req);
  const fullUrl = originalUrl;
  const frontendSettings =
    reduxStore?.getState?.()?.storage?.frontendSettings
      ?.dealerCorporateSettings || {};

  const isAbleToLogin = frontendSettings?.enableAnonymousSearch !== "false";
  const registrationEnabled = frontendSettings?.enableRegistration === "true";

  if (isUnAllowedRegisterPage({ registrationEnabled, urlPath })) {
    return redirectTo(res, "/login");
  }

  if (
    isUnloggedPage({ fullUrl, urlPath }) &&
    !isAbleToLogin &&
    !(await isLogged({ req, res, golApiUrl }))
  ) {
    clearCustomerSessionCookies(req, res);
    const langQuery = req?.query?.lang;
    const lang = typeof langQuery === "string" ? `&lang=${langQuery}` : "";
    return redirectTo(res, `/login?isLoginRequired=true${lang}`);
  }

  if (isProtectedPage(urlPath) && !(await isLogged({ req, res, golApiUrl }))) {
    if (hasAuthenticatedCustomerSessionMarker(req)) {
      clearCustomerSessionCookies(req, res);
    }
    return redirectTo(res, "/login");
  }

  await ensureValidCustomerToken({ req, res, golApiUrl });
  return res.headersSent;
}

module.exports = {
  getFullUrl: getFullUrlFunction,
  passwordProtection: passwordProtectionFunction,
  generateRandomNumber,
  generateRandomString,
  Logger,
};
